import { DataQueryParams } from 'common/src/query-params/DataQueryParams';
import type {
  IBulkDeleteBillsResponse,
  ICreateBillDto,
  IUpdateAllocationsDto,
  IUpdateBillDto,
} from 'common/src/types/bill.dtos';
import type { IBill, IPage } from 'common/src/types/interfaces';

import {
  API_BASE_PATHS,
  formContentType,
  jsonContentType,
} from 'portal/src/api/constants';
import formatQueryParams from 'portal/src/api/formatQueryParams';
import {
  bulkDeleteRequest,
  deleteRequest,
  getBlobRequest,
  getRequest,
  patchRequest,
  postRequest,
} from 'portal/src/api/requestTypes';

export const getBills = async (
  params: DataQueryParams = {},
): Promise<IPage<IBill>> => {
  const { filters = [] } = params;

  return await getRequest(
    `${API_BASE_PATHS.BILLS}`,
    formatQueryParams({ ...params, filters }),
  );
};

export const getBill = async (id: string): Promise<IBill> =>
  await getRequest(`${API_BASE_PATHS.BILLS}/${id}`, {});

export const addBill = async (dto: ICreateBillDto): Promise<string> =>
  await postRequest(API_BASE_PATHS.BILLS, {}, dto);

export const updateBill = async (
  id: string,
  dto: IUpdateBillDto,
): Promise<void | string> =>
  await patchRequest(`${API_BASE_PATHS.BILLS}/${id}`, {}, dto);

export const deleteBill = async (id: string): Promise<void> =>
  await deleteRequest(`${API_BASE_PATHS.BILLS}/${id}`, {});

export const bulkDeleteBills = async (
  billIds: string[],
): Promise<IBulkDeleteBillsResponse> =>
  await bulkDeleteRequest(`${API_BASE_PATHS.BILLS}/bulk`, { billIds });

export const getBillImage = async (id: string): Promise<Blob> =>
  await getBlobRequest(`${API_BASE_PATHS.BILLS}/${id}/image`);

export const addBillImage = async (
  id: string,
  body: { file: File },
): Promise<void> =>
  await postRequest(
    `${API_BASE_PATHS.BILLS}/${id}/image`,
    {},
    body,
    formContentType,
  );

export const deleteBillImage = async (id: string): Promise<void> =>
  await deleteRequest(`${API_BASE_PATHS.BILLS}/${id}/image`);

export const allocateMasterBill = async (billId: string): Promise<void> =>
  await postRequest(`${API_BASE_PATHS.BILLS}/${billId}/allocate`, {});

export const previewAllocation = async (
  dto: ICreateBillDto,
): Promise<Partial<IBill[]>> =>
  await postRequest(
    `${API_BASE_PATHS.BILLS}/allocation-preview`,
    {},
    dto,
    jsonContentType,
    true,
  );

export const updateBillAllocations = async (
  billId: string,
  dto: IUpdateAllocationsDto,
): Promise<void> =>
  await patchRequest(`${API_BASE_PATHS.BILLS}/${billId}/allocate`, {}, dto);

export const dismissAllocationReview = async (
  billId: string,
  dto: { allocationsEdited: boolean },
): Promise<void> =>
  await patchRequest(
    `${API_BASE_PATHS.BILLS}/${billId}/dismiss-allocation-review`,
    {},
    dto,
  );
